<template>
  <section class="why-matters">
    <div class="container">
      <div class="header-content reveal">
        <h2 class="section-title">
          You Shouldn't Have to Choose Between<br />
          <span class="highlight">Running Your Business and Growing It</span>
        </h2>
        <p class="section-desc">
          Bizzento bridges the gap between the daily grind and your long-term
          vision.
        </p>
      </div>

      <div class="benefit-grid">
        <div class="benefit-card reveal delay-1">
          <div class="card-header">
            <div class="icon-frame">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <circle cx="12" cy="12" r="6" />
                <circle cx="12" cy="12" r="2" />
              </svg>
            </div>
            <h3>Stay Focused</h3>
          </div>
          <p>
            While Bizzento handles the 24/7 stream of inquiries and bookings,
            you reclaim the mental space to do what you love—bake, create, and
            serve.
          </p>
        </div>

        <div class="benefit-card reveal delay-2">
          <div class="card-header">
            <div class="icon-frame">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M2 3h20" />
                <path d="M21 3v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V3" />
                <path d="m7 21 5-5 5 5" />
              </svg>
            </div>
            <h3>See Clearly</h3>
          </div>
          <p>
            Stop drowning in spreadsheets. Bizzento translates complex data into
            simple, visual patterns so you always know exactly where your
            revenue is coming from.
          </p>
        </div>

        <div class="benefit-card reveal delay-3">
          <div class="card-header">
            <div class="icon-frame">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
            </div>
            <h3>Decide Confidently</h3>
          </div>
          <p>
            No more "gut-feeling" guesses. Get strategic insights backed by your
            actual business data and industry benchmarks to make moves that
            stick.
          </p>
        </div>
      </div>

      <div class="bottom-cta reveal delay-3">
        <p>Ready to reclaim your time?</p>
        <a href="#waitlist" class="secondary-link">Join the waitlist today →</a>
      </div>
    </div>
  </section>
</template>

<style scoped>
.why-matters {
  --bg-warm-base: #f2eee0;
  --sunlight-glow: #faefca;
  --soft-gold: #f5e5b0;
  --dark-accent: #1a1a1a;
  --text-muted: #8e8a82;
  --gold-highlight: #bfa14d;

  padding: 120px 24px;
  background-color: var(--bg-warm-base);
  color: var(--dark-accent);
}

.container {
  max-width: 1100px;
  margin: 0 auto;
}

.header-content {
  text-align: center;
  margin-bottom: 80px;
}

.section-title {
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 20px;
}

.highlight {
  color: var(--gold-highlight);
}

.section-desc {
  font-size: 1.2rem;
  color: var(--text-muted);
}

.benefit-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 32px;
}

.benefit-card {
  background: white;
  padding: 48px;
  border-radius: 32px;
  transition: all 0.3s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.02);
  text-align: left; /* Alignment fix: Left-aligned looks more professional for grids */
}

.benefit-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.icon-frame {
  width: 44px;
  height: 44px;
  background: var(--sunlight-glow);
  color: var(--gold-highlight);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  flex-shrink: 0;
}

h3 {
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
}

.benefit-card p {
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--text-muted);
  margin: 0;
}

.bottom-cta {
  text-align: center;
  margin-top: 80px;
  border-top: 1px solid var(--soft-gold);
  padding-top: 48px;
}

.bottom-cta p {
  font-weight: 600;
  margin-bottom: 12px;
}

.secondary-link {
  color: var(--dark-accent);
  text-decoration: none;
  font-weight: 800;
  font-size: 1.1rem;
  transition: color 0.2s;
  display: inline-block;
}

.secondary-link:hover {
  color: var(--gold-highlight);
  transform: translateX(4px);
}

@media (max-width: 768px) {
  .why-matters {
    padding: 80px 20px;
  }
  .benefit-card {
    padding: 32px;
  }
  .card-header {
    flex-direction: row;
    align-items: center;
  }
}
</style>
